import { useState, useEffect } from "react";
import PasswordGate from "../components/PasswordGate";
import { formatRelativeTime } from "../utils/formatRelativeTime";

const LOG_LEVEL_COLORS = {
  ERROR: "text-red-500",
  WARNING: "text-amber-500",
  SUCCESS: "text-emerald-500",
  INFO: "text-blue-400",
  DEBUG: "text-slate-400",
};

function StatusBadge({ status }) {
  const styles = {
    new: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    read: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
    resolved: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.new}`}>
      {status}
    </span>
  );
}

function StatCard({ label, value, active }) {
  return (
    <div className={`rounded-xl border p-5 cursor-pointer transition-colors ${
      active
        ? "border-primary-400 bg-primary-50 dark:bg-primary-900/20"
        : "border-slate-200 dark:border-slate-800 bg-white dark:bg-surface-card-dark hover:border-slate-300 dark:hover:border-slate-700"
    }`}>
      <div className="text-sm text-slate-500 dark:text-slate-400 mb-1">{label}</div>
      <div className="text-3xl font-bold text-slate-900 dark:text-white">
        {typeof value === "number" ? value.toLocaleString() : value ?? 0}
      </div>
    </div>
  );
}

function LogEntryViewer({ entries }) {
  if (!entries || entries.length === 0) {
    return <p className="text-slate-400 text-sm py-4">No log entries</p>;
  }

  return (
    <div className="max-h-[500px] overflow-y-auto rounded-lg bg-slate-950 p-4 font-mono text-xs leading-relaxed">
      {entries.map((entry, i) => {
        const timeStr = entry.timestamp
          ? new Date(entry.timestamp).toLocaleTimeString()
          : "";
        const levelColor = LOG_LEVEL_COLORS[entry.level] || "text-slate-300";
        return (
          <div key={i} className="flex gap-2 hover:bg-slate-900/50 px-1 -mx-1 rounded">
            <span className="text-slate-500 shrink-0">{timeStr}</span>
            <span className={`font-bold shrink-0 ${levelColor}`}>{entry.level}:</span>
            <span className="text-slate-300 break-all">{entry.message}</span>
            {entry.source && (
              <span className="text-slate-600 shrink-0">({entry.source})</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SubmissionDetail({ submission, password, onStatusChange }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState(submission.notes || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setNotes(submission.notes || "");
    loadDetail();
  }, [submission.id]);

  const loadDetail = async () => {
    setLoading(true);
    try {
      const response = await fetch("/api/get-console-log-detail.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password, id: submission.id }),
      });
      if (response.ok) {
        setDetail(await response.json());
      }
    } catch {
      // Silently fail - list view data is still available
    } finally {
      setLoading(false);
    }
  };

  const updateSubmission = async (fields) => {
    setSaving(true);
    try {
      const response = await fetch("/api/update-console-log.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password, id: submission.id, ...fields }),
      });
      if (response.ok) {
        onStatusChange();
      }
    } catch {
      // Silently fail
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-surface-card-dark p-5">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="font-semibold text-slate-900 dark:text-white">
            Submission #{submission.id}
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {formatRelativeTime(submission.timestamp)} &middot; v{submission.app_version} &middot;{" "}
            {submission.os_name} {submission.os_version}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={submission.status}
            onChange={(e) => updateSubmission({ status: e.target.value })}
            disabled={saving}
            className="px-2 py-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-700 dark:text-slate-300"
          >
            <option value="new">New</option>
            <option value="read">Read</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>
      </div>

      {submission.description && (
        <div className="mb-4 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
          <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">User Description</div>
          <p className="text-sm text-slate-700 dark:text-slate-300">{submission.description}</p>
        </div>
      )}

      <div className="mb-4">
        <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
          Developer Notes
        </div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={() => {
            if (notes !== (submission.notes || "")) {
              updateSubmission({ notes });
            }
          }}
          placeholder="Add notes..."
          rows={2}
          className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-700 dark:text-slate-300 placeholder-slate-400 resize-none"
        />
      </div>

      <div>
        <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">
          Log Entries ({submission.entry_count})
        </div>
        {loading ? (
          <p className="text-slate-400 text-sm py-4">Loading log entries...</p>
        ) : (
          <LogEntryViewer entries={detail?.log_entries} />
        )}
      </div>
    </div>
  );
}

export default function ConsoleLogs() {
  const [password, setPassword] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [versionFilter, setVersionFilter] = useState("");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState(null);

  const loadSubmissions = async (pw, opts = {}) => {
    setLoading(true);
    setError("");
    try {
      const body = {
        password: pw,
        page: opts.page || page,
        per_page: 20,
      };
      if (opts.status || statusFilter) body.status = opts.status || statusFilter;
      if (opts.version || versionFilter) body.version = opts.version || versionFilter;

      const response = await fetch("/api/get-console-logs.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (response.status === 401) {
        setPassword(null);
        setError("Session expired. Please re-enter password.");
        return;
      }

      if (!response.ok) throw new Error("Failed to load");

      setData(await response.json());
    } catch {
      setError("Failed to load console log submissions.");
    } finally {
      setLoading(false);
    }
  };

  const handleAuthenticate = (pw) => {
    setPassword(pw);
    loadSubmissions(pw);
  };

  const handleFilterChange = (type, value) => {
    const newOpts = { page: 1 };
    if (type === "status") {
      setStatusFilter(value);
      newOpts.status = value;
      newOpts.version = versionFilter;
    } else {
      setVersionFilter(value);
      newOpts.version = value;
      newOpts.status = statusFilter;
    }
    setPage(1);
    setSelectedId(null);
    loadSubmissions(password, newOpts);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setSelectedId(null);
    loadSubmissions(password, { page: newPage, status: statusFilter, version: versionFilter });
  };

  if (!password) {
    return <PasswordGate onAuthenticate={handleAuthenticate} title="Console Logs" description="Enter password to view log submissions." />;
  }

  if (loading && !data) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-slate-500 dark:text-slate-400">Loading console logs...</div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={() => loadSubmissions(password)}
            className="px-4 py-2 bg-primary-400 hover:bg-primary-500 text-white rounded-lg text-sm font-medium transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const counts = data.status_counts || {};
  const total = (counts.new || 0) + (counts.read || 0) + (counts.resolved || 0);
  const selectedSubmission = selectedId
    ? data.submissions.find((s) => s.id === selectedId) || null
    : null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-1">Console Logs</h1>
          {data.generatedAt && (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Last updated {formatRelativeTime(data.generatedAt)}
            </p>
          )}
        </div>
        <button
          onClick={() => loadSubmissions(password, { page, status: statusFilter, version: versionFilter })}
          disabled={loading}
          className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg text-sm transition-colors text-slate-700 dark:text-slate-300 disabled:opacity-50"
        >
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {error && (
        <div className="mb-6 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div onClick={() => handleFilterChange("status", "")}>
          <StatCard label="Total" value={total} active={statusFilter === ""} />
        </div>
        <div onClick={() => handleFilterChange("status", "new")}>
          <StatCard label="New" value={counts.new || 0} active={statusFilter === "new"} />
        </div>
        <div onClick={() => handleFilterChange("status", "read")}>
          <StatCard label="Read" value={counts.read || 0} active={statusFilter === "read"} />
        </div>
        <div onClick={() => handleFilterChange("status", "resolved")}>
          <StatCard label="Resolved" value={counts.resolved || 0} active={statusFilter === "resolved"} />
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-6">
        {data.versions && data.versions.length > 0 && (
          <select
            value={versionFilter}
            onChange={(e) => handleFilterChange("version", e.target.value)}
            className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-700 dark:text-slate-300"
          >
            <option value="">All Versions</option>
            {data.versions.map((v) => (
              <option key={v} value={v}>v{v}</option>
            ))}
          </select>
        )}
        <span className="text-sm text-slate-400">
          {data.pagination.total} submission{data.pagination.total !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Main content: list + detail */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Submissions list */}
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-surface-card-dark p-5">
          <h2 className="font-semibold text-slate-900 dark:text-white mb-4">Submissions</h2>
          {data.submissions.length > 0 ? (
            <div className="space-y-1">
              {data.submissions.map((sub) => (
                <div
                  key={sub.id}
                  onClick={() => setSelectedId(sub.id)}
                  className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors text-sm ${
                    selectedId === sub.id
                      ? "bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800"
                      : "hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  }`}
                >
                  <div className="min-w-0 flex-1 mr-3">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-slate-700 dark:text-slate-300 font-medium">
                        #{sub.id}
                      </span>
                      <StatusBadge status={sub.status} />
                      <span className="text-slate-400 text-xs">v{sub.app_version}</span>
                    </div>
                    <p className="text-slate-500 dark:text-slate-400 text-xs truncate">
                      {sub.description || `${sub.entry_count} entries \u2022 ${sub.os_name || "Unknown OS"}`}
                    </p>
                  </div>
                  <span className="text-slate-400 text-xs whitespace-nowrap shrink-0">
                    {formatRelativeTime(sub.timestamp)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-400 text-center py-6 text-sm">No submissions found</p>
          )}

          {/* Pagination */}
          {data.pagination.total_pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page <= 1}
                className="px-2.5 py-1 rounded-lg text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30 transition-colors"
              >
                Prev
              </button>
              <span className="text-sm text-slate-500 dark:text-slate-400 tabular-nums">
                {page} / {data.pagination.total_pages}
              </span>
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= data.pagination.total_pages}
                className="px-2.5 py-1 rounded-lg text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30 transition-colors"
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* Detail panel */}
        {selectedSubmission ? (
          <SubmissionDetail
            key={selectedSubmission.id}
            submission={selectedSubmission}
            password={password}
            onStatusChange={() => loadSubmissions(password, { page, status: statusFilter, version: versionFilter })}
          />
        ) : (
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-surface-card-dark p-5 flex items-center justify-center min-h-[300px]">
            <p className="text-slate-400 text-sm">Select a submission to view details</p>
          </div>
        )}
      </div>
    </div>
  );
}
